import { Component, Inject, Input, OnChanges } from '@angular/core';
import { WINDOW } from '@ng-web-apis/common';
import { ICellRendererParams } from '@ag-grid-community/core';
import { ShareClass } from '@models';
import { createNavigationString } from '@utils/text/string-utils';
import { LiteratureDocumentListingData } from '@types';

export interface LitProperties {
  pdfDownloadLink: string;
  dctermsType: string;
  imageUrl: string;
  fileExtension: string;
  shareclass: {
    previewLink: string;
    shareClassName?: string;
    shareClassCode?: string;
  };
}
const THUMBNAIL_TYPES = {
  presentation: 'PRESENTATION',
  postcard: 'POSTCARD',
  pitchbook: 'PRODUCT-PITCHBOOK',
};

export interface LitAgGridCellParams extends ICellRendererParams {
  data: {
    shareClass: string[];
    literatureData: { [key: string]: LitProperties } & {
      loginRequired?: boolean;
      fileExtension?: string;
      thumbnailPath?: string;
      dctermsType?: string;
    };
    product?: { shareClasses: ShareClass[] };
  };
  isPreviewLink: boolean;
  isSiteIntl: boolean;
  showOnlyIcon: boolean;
  colDef: { colId: string };
}
@Component({
  selector: 'ft-icon-column',
  templateUrl: './icon-column.component.html',
  styleUrls: ['./icon-column.component.scss'],
})
export class IconColumnComponent implements OnChanges {
  @Input() cellRendererData: LitAgGridCellParams;
  /**
   * Params
   */
  public params: LitAgGridCellParams;
  public isSiteIntl;
  public thumbnailAvailable;
  public targetLink;
  public isCustom: boolean;
  public isDocumentAvailable = false;
  public regulatoryDocumentFileExtension: string;
  public showOnlyIcon: boolean;
  public isPreviewLink = false;
  public loginRequired: boolean;
  public isShowDownload: boolean;
  public imageLayout: string;
  constructor(@Inject(WINDOW) readonly windowRef: Window) {}

  ngOnChanges(): void {
    if (this.cellRendererData) {
      this.agInit(this.cellRendererData);
    }
  }

  agInit(params: LitAgGridCellParams) {
    this.params = params;
    this.thumbnailAvailable = params?.data?.literatureData?.imageUrl
      ? true
      : false;
    this.isSiteIntl = params.isSiteIntl;
    this.showOnlyIcon = params.showOnlyIcon;
    this.isCustom = params.data?.shareClass ? true : false;
    if (this.isCustom) {
      this.isPreviewLink = params.isPreviewLink;
      this.isDocumentAvailable =
        params.colDef.colId in params.data.literatureData &&
        params.data.literatureData[params.colDef.colId].pdfDownloadLink
          ?.length > 0;
      this.regulatoryDocumentFileExtension = params.data.literatureData[
        params.colDef.colId
      ]?.fileExtension
        ? params.data.literatureData[params.colDef.colId].fileExtension
        : 'pdf';
    }
    if (!this.isSiteIntl) {
      this.loginRequired = this.params?.data?.literatureData?.loginRequired;
      this.isDownloadAvailable();
    }
    // by default it is always portrait and onImageLoad() will replace it to landscape if needed
    if (!this.isSiteIntl) {
      this.imageLayout = 'portrait';
    }
  }

  openDocument(event, params: LitAgGridCellParams) {
    event.preventDefault();
    if (params.colDef.colId in params.data.literatureData) {
      const documentLink = this.getDocumentLink(params);
      this.windowRef.open(documentLink, '_blank');
    }
  }
  /**
   *  Checks if download is available for a document based on the printOnly field and downloadUrl values
   */

  private isDownloadAvailable(): void {
    // covers scenarios for printOnly as 'Y', 'N' and undefined
    this.isShowDownload = this.params?.data?.literatureData.downloadUrl
      ? true
      : false;
  }
  openImageDoc(literatureListingData: LiteratureDocumentListingData) {
    const documentLink = this.getDownloadLinkWithPreview(literatureListingData);
    this.windowRef.open(documentLink, '_blank');
  }
  /**
   * returns download link or preview link if document is configured in literature site config preview
   * @param params ag-grid configuration with literature data and config
   */
  getDocumentLink(params: LitAgGridCellParams): string {
    const literatureDocument: LitProperties =
      params.data.literatureData[params.colDef.colId];
    let documentLink = literatureDocument.pdfDownloadLink;

    if (this.isPreviewLink) {
      let documentSuffix: string = createNavigationString(params.colDef.colId);
      if (documentSuffix === 'q1-holding') {
        documentSuffix = 'fiscal-q1-holdings';
      }
      if (documentSuffix === 'q3-holding') {
        documentSuffix = 'fiscal-q3-holdings';
      }

      // handle link formation for single share class row in custom listing
      if (
        params.data.shareClass.length === 1 &&
        params.data.shareClass[0] !== '-' &&
        params.data.shareClass[0] !==
          literatureDocument.shareclass.shareClassName
      ) {
        let currentShareClassCode: string;
        params.data.product?.shareClasses.forEach((data) => {
          if (data.shareClassName === params.data.shareClass[0]) {
            currentShareClassCode = data.shareClassCode;
          }
        });
        documentLink =
          this.getReplaceShareClassCode(
            literatureDocument.shareclass.previewLink,
            literatureDocument.shareclass.shareClassCode,
            currentShareClassCode
          ) +
          '#' +
          documentSuffix;
      } else {
        documentLink =
          literatureDocument.shareclass.previewLink + '#' + documentSuffix;
      }
    }
    return documentLink;
  }

  getReplaceShareClassCode(
    previewLink: string,
    oldShareClassCode: string,
    newShareClassCode: string
  ): string {
    // Replace /oldShareClassCode/ with the newShareClassCode
    return previewLink.replace(
      new RegExp(`/${oldShareClassCode}/`),
      `/${newShareClassCode}/`
    );
  }

  onImageLoad($event: any) {
    // TODO - test and consider if we can find parent div element in a cleaner way.

    if ($event.target?.naturalHeight < $event.target?.naturalWidth) {
      const parentDiv = $event.target?.closest('div');
      $event.target.classList.remove('img--expand');
      $event.target.classList.remove('img--full-height');
      parentDiv?.classList?.remove('icon-column--portrait');
      parentDiv?.classList?.add('icon-column--landscape');
    }
  }

  /**
   * Get download link preview
   */
  public getDownloadLinkWithPreview(
    data: LiteratureDocumentListingData
  ): string {
    return data.literatureData?.usePreviewLink &&
      data?.literatureData?.shareclass?.previewLink &&
      data?.literatureData?.dctermsType
      ? data?.literatureData?.shareclass?.previewLink +
          '#' +
          createNavigationString(data?.literatureData?.dctermsType)
      : data?.literatureData?.pdfDownloadLink;
  }
}
